import React, { useState } from "react"

import { format } from "date-fns"
import { CalendarIcon, CheckCircle2, Download, SlidersHorizontal } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { FinancialImpactFilter, TicketTypeOption } from "@/hooks/useTickets"
import { cn } from "@/lib/utils"

export const TICKET_STATUSES = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Denied", label: "Denied" },
] as const

const FINANCIAL_IMPACT_OPTIONS: Array<{
  value: FinancialImpactFilter
  label: string
}> = [
  { value: "all", label: "All" },
  { value: "with", label: "With financial impact" },
  { value: "without", label: "Without financial impact" },
]

interface TicketsFiltersProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  financialImpactFilter: FinancialImpactFilter
  onFinancialImpactFilterChange: (value: FinancialImpactFilter) => void
  statusFilter: string
  onStatusFilterChange: (value: string) => void
  ticketTypeIdFilter: number | null
  onTicketTypeIdFilterChange: (value: number | null) => void
  ticketTypeOptions: TicketTypeOption[]
  driverManagerTeamFilter: string
  onDriverManagerTeamFilterChange: (value: string) => void
  driverManagerTeamOptions: string[]
  visibleCount: number
  totalCount: number
  startDate: Date
  endDate: Date
  onStartDateChange: (value: Date) => void
  onEndDateChange: (value: Date) => void
  onExport: () => void
  isExporting: boolean
  disableExport: boolean
}

const TicketsFilters: React.FC<TicketsFiltersProps> = ({
  searchQuery,
  onSearchChange,
  financialImpactFilter,
  onFinancialImpactFilterChange,
  statusFilter,
  onStatusFilterChange,
  ticketTypeIdFilter,
  onTicketTypeIdFilterChange,
  ticketTypeOptions,
  driverManagerTeamFilter,
  onDriverManagerTeamFilterChange,
  driverManagerTeamOptions,
  visibleCount,
  totalCount,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onExport,
  isExporting,
  disableExport,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false)
  const activeFilterCount = [
    Boolean(financialImpactFilter && financialImpactFilter !== "all"),
    Boolean(statusFilter && statusFilter !== "all"),
    ticketTypeIdFilter != null,
    Boolean(driverManagerTeamFilter && driverManagerTeamFilter !== "all"),
  ].filter(Boolean).length

  return (
    <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm p-4 rounded-lg shadow-sm mb-6 border space-y-3">
      <div className="flex flex-wrap gap-4 items-end">
        {/* Search first */}
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
            Search
          </label>
          <Input
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="flex flex-none min-w-[220px] flex-col gap-2">
          <label className="text-sm font-medium text-palette-jungleGreen700">
            Financial impact
          </label>
          <Select
            value={financialImpactFilter}
            onValueChange={(value) => {
              const option = FINANCIAL_IMPACT_OPTIONS.find((o) => o.value === value)
              if (option) onFinancialImpactFilterChange(option.value)
            }}
          >
            <SelectTrigger className="w-full sm:w-[240px]">
              <SelectValue placeholder="All tickets" />
            </SelectTrigger>
            <SelectContent>
              {FINANCIAL_IMPACT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Combined date range */}
        <div className="flex flex-none min-w-[280px] flex-col gap-2">
          <label className="text-sm font-medium text-palette-jungleGreen700">Date range</label>
          <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[220px] justify-start text-left font-normal",
                    !startDate && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "PPP") : "Start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  defaultMonth={startDate}
                  onSelect={(date) => date && onStartDateChange(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <div className="self-center text-sm text-palette-jungleGreen500">to</div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[220px] justify-start text-left font-normal",
                    !endDate && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "PPP") : "End date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  defaultMonth={endDate}
                  onSelect={(date) => date && onEndDateChange(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              size="default"
              onClick={() => {
                const today = new Date()
                onStartDateChange(today)
                onEndDateChange(today)
              }}
            >
              Today
            </Button>
          </div>
        </div>

        <div className="ml-auto flex gap-2 items-center">
          <Button variant="outline" onClick={onExport} disabled={isExporting || disableExport}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="default" className="gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0 text-xs"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end">
              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="font-medium text-sm text-palette-jungleGreen900">
                    Filter Tickets
                  </h4>
                  <p className="text-xs text-palette-jungleGreen500">
                    Refine results with filters below
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-palette-jungleGreen700">
                    Financial impact
                  </label>
                  <Select
                    value={financialImpactFilter}
                    onValueChange={(value) => {
                      const option = FINANCIAL_IMPACT_OPTIONS.find((o) => o.value === value)
                      if (option) onFinancialImpactFilterChange(option.value)
                    }}
                  >
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="All tickets" />
                    </SelectTrigger>
                    <SelectContent>
                      {FINANCIAL_IMPACT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-palette-jungleGreen700 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-palette-jungleGreen500" />
                    Status
                  </label>
                  <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {TICKET_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-palette-jungleGreen700">
                    Driver Manager Team
                  </label>
                  <Select
                    value={driverManagerTeamFilter}
                    onValueChange={onDriverManagerTeamFilterChange}
                  >
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="All teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All teams</SelectItem>
                      {driverManagerTeamOptions.map((team) => (
                        <SelectItem key={team} value={team}>
                          {team}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-palette-jungleGreen700">
                    Ticket name
                  </label>
                  <Select
                    value={ticketTypeIdFilter == null ? "all" : String(ticketTypeIdFilter)}
                    onValueChange={(value) =>
                      onTicketTypeIdFilterChange(value === "all" ? null : Number(value))
                    }
                  >
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="All ticket names" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All ticket names</SelectItem>
                      {ticketTypeOptions.map((option) => (
                        <SelectItem key={option.id} value={String(option.id)}>
                          {option.ticketName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="w-full text-sm text-palette-jungleGreen600">
        Showing {visibleCount.toLocaleString()} of {totalCount.toLocaleString()} tickets
      </div>
    </div>
  )
}

export default TicketsFilters
